import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DropzoneInputProps, DropzoneRootProps, useDropzone } from 'react-dropzone';
import ButtonLoading from '@mui/lab/LoadingButton';
import {
  Stack,
  Typography,
  TextField,
  Box,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { useDataInputFileUpload } from 'services/v1/Tenant/AROKMS/DisplayTableService';
import { getDropRejectErrorCode, getDropFileErrorMessageByCode } from 'utils/Error';
import { DATA_INPUT_OBJECT_TYPE } from 'constant/DataInputConstant';
import { AxiosDefaultErrorEntity } from 'types';
import { isValidUrl, isValidYoutubeUrl } from 'utils/String';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DataInputObjectUploadProps {
  name: string;
  onUploadComplete?: (objectIds: string[]) => void;
  subjectId?: string;
  label: string;
  objectType: string | null;
}

interface UploadItem {
  id: string;
  displayName: string;
  file: File | null;
  youtubeLink?: string;
  externalLink?: string;
  status: 'uploading' | 'success' | 'error';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  textInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      fontSize: 13,
    },
  },
  radioButtonLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '13px',
      color: '#667085',
    },
  },
  radioButton: {
    color: '#98A2AE',
    '& .MuiSvgIcon-root': { fontSize: 18 },
    '&.Mui-checked': { color: '#3B4797' },
  },
  dropzone: {
    border: '2px dashed #D0D5DD',
    borderRadius: 3,
    py: 4,
    px: 3,
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    bgcolor: '#FAFBFC',
    '&:hover': {
      borderColor: '#3B4797',
      bgcolor: 'rgba(59, 71, 151, 0.03)',
    },
  },
  dropzoneActive: {
    border: '2px dashed #3B4797',
    bgcolor: 'rgba(59, 71, 151, 0.06)',
  },
  linkInputRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    mt: 1.5,
  },
  uploadItemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: 1,
    borderRadius: 2,
    border: '1px solid #E8ECF1',
    bgcolor: '#FAFBFC',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAcceptedFileByObject(objectType: string | null) {
  switch (objectType) {
    case DATA_INPUT_OBJECT_TYPE.IMAGE:
      return { 'image/*': ['.jpg', '.jpeg', '.png'] };
    case DATA_INPUT_OBJECT_TYPE.VIDEO:
      return { 'video/*': ['.mp4'] };
    case DATA_INPUT_OBJECT_TYPE.DOCUMENT:
      return { 'application/pdf': ['.pdf'] };
    default:
      return { 'image/*': ['.jpg', '.jpeg', '.png'] };
  }
}

function getFileExtensionDescriptionByObject(objectType: string | null) {
  switch (objectType) {
    case DATA_INPUT_OBJECT_TYPE.IMAGE:
      return 'JPG, JPEG, or PNG';
    case DATA_INPUT_OBJECT_TYPE.VIDEO:
      return 'MP4';
    case DATA_INPUT_OBJECT_TYPE.DOCUMENT:
      return 'PDF';
    default:
      return 'JPG, JPEG, or PNG';
  }
}

function getTitleUploadByObjectType(objectType: string | null) {
  switch (objectType) {
    case DATA_INPUT_OBJECT_TYPE.IMAGE:
      return { title: 'Upload Images', icon: <ImageOutlinedIcon sx={{ fill: '#6C63FF', fontSize: 20 }} /> };
    case DATA_INPUT_OBJECT_TYPE.VIDEO:
      return { title: 'Upload Video', icon: <VideocamOutlinedIcon sx={{ fill: '#E74C3C', fontSize: 20 }} /> };
    case DATA_INPUT_OBJECT_TYPE.DOCUMENT:
      return { title: 'Upload Document', icon: <ArticleOutlinedIcon sx={{ fill: '#E67E22', fontSize: 20 }} /> };
    case DATA_INPUT_OBJECT_TYPE.LINK:
      return { title: 'Add External Link', icon: <AddLinkOutlinedIcon sx={{ fill: '#2980B9', fontSize: 20 }} /> };
    default:
      return { title: 'Upload Images', icon: <ImageOutlinedIcon sx={{ fill: '#6C63FF', fontSize: 20 }} /> };
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const VIDEO_UPLOAD_TYPE = {
  UPLOAD_VIDEO: 'UPLOAD_VIDEO',
  YOUTUBE_VIDEO: 'YOUTUBE_VIDEO',
};

const VIDEO_UPLOAD_OPTIONS = [
  { label: 'Upload from computer', value: VIDEO_UPLOAD_TYPE.UPLOAD_VIDEO },
  { label: 'Insert YouTube link', value: VIDEO_UPLOAD_TYPE.YOUTUBE_VIDEO },
];

const MAX_FILE_SIZE = 40 * 1024 * 1024;

// ─── Main Component ──────────────────────────────────────────────────────────

export function DataInputObjectUpload(props: DataInputObjectUploadProps) {
  const { name = 'input-upload', onUploadComplete = () => {}, subjectId, objectType } = props;
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [selectedVideoUploadType, setSelectedVideoUploadType] = useState(VIDEO_UPLOAD_TYPE.UPLOAD_VIDEO);
  const [inputYoutubeLink, setInputYoutubeLink] = useState<string>('');
  const [inputExternalLink, setInputExternalLink] = useState<string>('');
  const { mutateAsync: uploadObjectFile, isError, error } = useDataInputFileUpload();

  const isUploadingRef = useRef(false);

  const uploadImmediately = useCallback(
    async (items: { objectName: string; file: File | null; youtubeLink?: string; externalLink?: string }[]) => {
      if (items.length === 0 || !subjectId || !objectType || isUploadingRef.current) return;

      isUploadingRef.current = true;

      const newUploadItems: UploadItem[] = items.map((item, i) => ({
        id: `${Date.now()}-${i}`,
        displayName: item.objectName || item.youtubeLink || item.externalLink || 'File',
        file: item.file,
        youtubeLink: item.youtubeLink,
        externalLink: item.externalLink,
        status: 'uploading' as const,
      }));

      setUploadItems((prev) => [...prev, ...newUploadItems]);

      const uploadPromises = newUploadItems.map(async (uploadItem, i) => {
        const item = items[i];
        const formData = new FormData();
        formData.append('subjectId', subjectId);
        formData.append('url', '');
        formData.append('objectType', objectType);
        formData.append('objectName', item.objectName);
        formData.append('description', '');
        formData.append('objectId', '');
        formData.append('file', '');

        if (item.file) {
          formData.append('file', item.file);
        }
        if (item.youtubeLink) {
          formData.delete('url');
          formData.append('url', item.youtubeLink);
        }
        if (item.externalLink) {
          formData.delete('url');
          formData.append('url', item.externalLink);
        }

        try {
          const result = await uploadObjectFile(formData);
          setUploadItems((prev) =>
            prev.map((u) => (u.id === uploadItem.id ? { ...u, status: 'success' as const } : u)),
          );
          return result;
        } catch {
          setUploadItems((prev) => prev.map((u) => (u.id === uploadItem.id ? { ...u, status: 'error' as const } : u)));
          return null;
        }
      });

      const allResults = await Promise.allSettled(uploadPromises);
      const objectIds = allResults
        .map((r) => (r.status === 'fulfilled' && r.value ? r.value : null))
        // @ts-ignore
        .filter(Boolean)
        // @ts-ignore
        .map((r) => r?.data?.objectId)
        .filter((id): id is string => Boolean(id));

      if (objectIds.length > 0) {
        onUploadComplete(objectIds);
      }

      isUploadingRef.current = false;
    },
    [subjectId, objectType, uploadObjectFile, onUploadComplete],
  );

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setFileError(null);
        const items = acceptedFiles.map((file) => {
          const fileNameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
          return { objectName: fileNameWithoutExtension, file };
        });
        uploadImmediately(items);
      }
    },
    [uploadImmediately],
  );

  const onDropRejected = useCallback((rejectedFiles) => {
    setFileError(getDropFileErrorMessageByCode(getDropRejectErrorCode(rejectedFiles[0])));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    // @ts-ignore
    accept: getAcceptedFileByObject(objectType),
  });

  const handleOnInsertLinkYoutube = () => {
    if (!inputYoutubeLink) return;
    uploadImmediately([{ objectName: inputYoutubeLink, file: null, youtubeLink: inputYoutubeLink }]);
    setInputYoutubeLink('');
  };

  const handleOnInsertExternalLink = () => {
    if (!inputExternalLink) return;
    uploadImmediately([{ objectName: inputExternalLink, file: null, externalLink: inputExternalLink }]);
    setInputExternalLink('');
  };

  const uploadingCount = useMemo(() => uploadItems.filter((i) => i.status === 'uploading').length, [uploadItems]);

  useEffect(() => {
    return () => {
      setSelectedVideoUploadType(VIDEO_UPLOAD_TYPE.UPLOAD_VIDEO);
    };
  }, []);

  const titleConfig = getTitleUploadByObjectType(objectType);

  return (
    <Stack spacing={2}>
      {/* Section title */}
      <Stack direction='row' gap={1} alignItems='center'>
        {titleConfig.icon}
        <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797' }}>
          {titleConfig.title}
        </Typography>
      </Stack>

      {/* Video type selector */}
      {objectType === DATA_INPUT_OBJECT_TYPE.VIDEO && (
        <VideoUploadTypeSelector selectedType={selectedVideoUploadType} onTypeChange={setSelectedVideoUploadType} />
      )}

      {/* External link input */}
      {objectType === DATA_INPUT_OBJECT_TYPE.LINK && (
        <ExternalLinkInput
          inputExternalLink={inputExternalLink}
          setInputExternalLink={setInputExternalLink}
          handleOnInsertExternalLink={handleOnInsertExternalLink}
          isUploading={uploadingCount > 0}
        />
      )}

      {/* YouTube input */}
      {selectedVideoUploadType === VIDEO_UPLOAD_TYPE.YOUTUBE_VIDEO && objectType === DATA_INPUT_OBJECT_TYPE.VIDEO && (
        <YoutubeVideoInput
          inputYoutubeLink={inputYoutubeLink}
          setInputYoutubeLink={setInputYoutubeLink}
          handleOnInsertLinkYoutube={handleOnInsertLinkYoutube}
          isUploading={uploadingCount > 0}
        />
      )}

      {/* Dropzone */}
      {selectedVideoUploadType === VIDEO_UPLOAD_TYPE.UPLOAD_VIDEO && objectType !== DATA_INPUT_OBJECT_TYPE.LINK && (
        <FileUploadSection
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          name={name}
          objectType={objectType}
          isError={isError}
          error={error}
          fileError={fileError}
        />
      )}

      {/* Upload progress list */}
      {uploadItems.length > 0 && <UploadProgressList items={uploadItems} />}
    </Stack>
  );
}

// ─── Upload Progress List ────────────────────────────────────────────────────

function UploadProgressList({ items }: { items: UploadItem[] }) {
  const successCount = items.filter((i) => i.status === 'success').length;
  const errorCount = items.filter((i) => i.status === 'error').length;
  const uploadingCount = items.filter((i) => i.status === 'uploading').length;

  return (
    <Stack spacing={1}>
      <Stack direction='row' alignItems='center' gap={1}>
        <Typography variant='body2' fontWeight={700} sx={{ color: '#344054' }}>
          {uploadingCount > 0 ? 'Uploading...' : 'Upload complete'}
        </Typography>
        <Chip
          label={`${successCount}/${items.length}`}
          size='small'
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 700,
            bgcolor: errorCount > 0 ? '#FDEDEC' : '#E8F5E9',
            color: errorCount > 0 ? '#E74C3C' : '#2E7D32',
          }}
        />
      </Stack>

      {uploadingCount > 0 && (
        <LinearProgress
          variant='determinate'
          value={items.length > 0 ? (successCount / items.length) * 100 : 0}
          sx={{
            height: 4,
            borderRadius: 2,
            bgcolor: '#E8EAF6',
            '& .MuiLinearProgress-bar': { bgcolor: '#3B4797', borderRadius: 2 },
          }}
        />
      )}

      <Stack spacing={0.75} sx={{ maxHeight: 200, overflow: 'auto' }}>
        {items.map((item) => (
          <Box key={item.id} sx={styles.uploadItemRow}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: '#98A2AE', flexShrink: 0 }} />
            <Typography
              variant='caption'
              sx={{
                flex: 1,
                color: '#344054',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.displayName}
            </Typography>
            {item.file && (
              <Typography variant='caption' sx={{ color: '#98A2AE', fontSize: 10, flexShrink: 0 }}>
                {formatFileSize(item.file.size)}
              </Typography>
            )}
            {item.status === 'uploading' && (
              <Box sx={{ width: 16, height: 16, flexShrink: 0 }}>
                <LinearProgress sx={{ width: 16, height: 16, borderRadius: '50%' }} />
              </Box>
            )}
            {item.status === 'success' && (
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#42BB93', flexShrink: 0 }} />
            )}
            {item.status === 'error' && <ErrorOutlineIcon sx={{ fontSize: 16, color: '#E74C3C', flexShrink: 0 }} />}
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

// ─── Dropzone ────────────────────────────────────────────────────────────────

const FileUploadSection = ({
  getRootProps,
  getInputProps,
  isDragActive,
  name,
  objectType,
  isError,
  error,
  fileError,
}: {
  name: string;
  objectType: string | null;
  isError: boolean;
  isDragActive: boolean;
  getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  error: AxiosDefaultErrorEntity | null;
  fileError: string | null;
}) => (
  <>
    <Box
      {...getRootProps()}
      sx={{
        ...styles.dropzone,
        ...(isDragActive ? styles.dropzoneActive : {}),
      }}
    >
      <input {...getInputProps()} name={name} />
      <Stack alignItems='center' spacing={1}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: isDragActive ? 'rgba(59,71,151,0.1)' : '#F0F2F5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 24, color: isDragActive ? '#3B4797' : '#98A2AE' }} />
        </Box>
        <Stack alignItems='center' spacing={0.25}>
          <Typography variant='body2' fontWeight={600} sx={{ color: '#344054' }}>
            {isDragActive ? 'Drop your files here' : 'Drag & drop files here'}
          </Typography>
          <Typography variant='caption' sx={{ color: '#98A2AE' }}>
            or{' '}
            <Box component='span' sx={{ color: '#3B4797', fontWeight: 600, cursor: 'pointer' }}>
              browse files
            </Box>
            {' — uploads start automatically'}
          </Typography>
        </Stack>
        <Chip
          label={`${getFileExtensionDescriptionByObject(objectType)} · Max 40 MB`}
          size='small'
          sx={{ mt: 0.5, height: 22, fontSize: 11, bgcolor: '#F0F2F5', color: '#667085' }}
        />
      </Stack>
    </Box>
    {isError && (
      <Typography variant='caption' sx={{ color: '#E74C3C', mt: 0.5 }}>
        {error?.response?.data?.message}
      </Typography>
    )}
    {fileError && (
      <Typography variant='caption' sx={{ color: '#E74C3C', mt: 0.5 }}>
        {fileError}
      </Typography>
    )}
  </>
);

// ─── Video Type Selector ─────────────────────────────────────────────────────

const VideoUploadTypeSelector = ({
  selectedType,
  onTypeChange,
}: {
  selectedType: string;
  onTypeChange: (val: string) => void;
}) => (
  <RadioGroup onChange={(_, value) => onTypeChange(value)} value={selectedType}>
    <Stack direction='row' spacing={2}>
      {VIDEO_UPLOAD_OPTIONS.map((option) => (
        <FormControlLabel
          key={option.value}
          value={option.value}
          sx={styles.radioButtonLabel}
          control={<Radio size='small' sx={styles.radioButton} />}
          label={option.label}
        />
      ))}
    </Stack>
  </RadioGroup>
);

// ─── External Link Input ─────────────────────────────────────────────────────

const ExternalLinkInput = ({
  inputExternalLink,
  setInputExternalLink,
  handleOnInsertExternalLink,
  isUploading,
}: {
  inputExternalLink: string;
  setInputExternalLink: (val: string) => void;
  handleOnInsertExternalLink: () => void;
  isUploading: boolean;
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputExternalLink(value);
    if (value && !isValidUrl(value)) {
      setError('Please enter a valid URL');
    } else {
      setError(null);
    }
  };

  const handleInsertLink = () => {
    if (isValidUrl(inputExternalLink)) {
      handleOnInsertExternalLink();
      setError(null);
    } else {
      setError('Please enter a valid URL');
    }
  };

  return (
    <Box sx={styles.linkInputRow}>
      <TextField
        sx={{ ...styles.textInput, flex: 1 }}
        name='externalLink'
        value={inputExternalLink}
        onChange={handleChange}
        variant='outlined'
        size='small'
        placeholder='https://example.com'
        error={!!error}
        helperText={error}
        InputProps={{
          startAdornment: <LanguageOutlinedIcon sx={{ fontSize: 18, color: '#98A2AE', mr: 0.5 }} />,
        }}
      />
      <ButtonLoading
        variant='main-table-panel'
        onClick={handleInsertLink}
        disabled={!!error || !inputExternalLink}
        loading={isUploading}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', height: 40 }}
      >
        Add Link
      </ButtonLoading>
    </Box>
  );
};

// ─── YouTube Input ───────────────────────────────────────────────────────────

const YoutubeVideoInput = ({
  inputYoutubeLink,
  setInputYoutubeLink,
  handleOnInsertLinkYoutube,
  isUploading,
}: {
  inputYoutubeLink: string;
  setInputYoutubeLink: (val: string) => void;
  handleOnInsertLinkYoutube: () => void;
  isUploading: boolean;
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputYoutubeLink(value);
    if (value && !isValidYoutubeUrl(value)) {
      setError('Please enter a valid YouTube URL');
    } else {
      setError(null);
    }
  };

  const handleInsertLink = () => {
    if (isValidYoutubeUrl(inputYoutubeLink)) {
      handleOnInsertLinkYoutube();
      setError(null);
    } else {
      setError('Please enter a valid YouTube URL');
    }
  };

  return (
    <Box sx={styles.linkInputRow}>
      <TextField
        sx={{ ...styles.textInput, flex: 1 }}
        name='youtubeLink'
        value={inputYoutubeLink}
        onChange={handleChange}
        variant='outlined'
        size='small'
        placeholder='https://www.youtube.com/watch?v=...'
        error={!!error}
        helperText={error}
        InputProps={{
          startAdornment: <VideocamOutlinedIcon sx={{ fontSize: 18, color: '#98A2AE', mr: 0.5 }} />,
        }}
      />
      <ButtonLoading
        variant='main-table-panel'
        onClick={handleInsertLink}
        disabled={!inputYoutubeLink || !!error}
        loading={isUploading}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', height: 40 }}
      >
        Insert
      </ButtonLoading>
    </Box>
  );
};
